import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useDataCache } from '../contexts/DataCacheContext';
import Logo from "./Logo";

const Sidebar = () => {
  const location = useLocation();
  const { logout, isAdmin } = useAuth();
  const { prefetchSettings, prefetchBookings, prefetchLogs, prefetchSkills, prefetchPlans, prefetchReferrals, cache } = useDataCache();
  const [openSubmenu, setOpenSubmenu] = useState(null);

  const enabledModules = (cache.skills?.modules || []).reduce((acc, m) => {
    acc[m.module_id] = m.enabled;
    return acc;
  }, {});

  const menuItems = [
    { path: '/dashboard', label: 'Dashboard', icon: 'fa-chart-line' },
    { path: '/assistant', label: 'Assistant', icon: 'fa-robot' },
    { path: '/staff', label: 'Staff', icon: 'fa-users' },
    { path: '/services', label: 'Services', icon: 'fa-briefcase' },
    { path: '/bookings', label: 'Bookings', icon: 'fa-calendar' },
    { path: '/smart-dispatch', label: 'Smart Dispatch', icon: 'fa-route' },
    { path: '/settings', label: 'Settings', icon: 'fa-cog' },
    { path: '/logs', label: 'Interactions', icon: 'fa-file-text' },
    { path: '/skills', label: 'Skills', icon: 'fa-puzzle-piece' },
    { path: '/integrations', label: 'Integrations', icon: 'fa-plug' },
    { path: '/plans', label: 'Subscription', icon: 'fa-credit-card' },
  ];

  if (enabledModules['faq_knowledge']) {
    menuItems.splice(menuItems.findIndex(i => i.path === '/skills'), 0,
      { path: '/faq-sources', label: 'Knowledge Base', icon: 'fa-book' }
    );
  }

  if (isAdmin) {
    menuItems.push({ path: '/admin/customers', label: 'Admin', icon: 'fa-users-cog' });
  }

  const toggleSubmenu = (path) => {
    setOpenSubmenu(openSubmenu === path ? null : path);
  };

  return (
    <div className="w-60 h-screen flex flex-col fixed left-0 top-0" style={{background: 'linear-gradient(90deg, #222324 0%, #131313 100%)'}}>
      {/* Logo */}
      <Link to="/dashboard" className="p-6 flex items-center gap-3">
          <Logo />
      </Link>

      {/* Navigation */}
      <nav className="flex-1 px-3 mt-4">
        {menuItems.map((item) => {
          const isActive = location.pathname === item.path || (item.submenu && item.submenu.some(sub => location.pathname === sub.path));
          const hasSubmenu = item.submenu && item.submenu.length > 0;
          const isSubmenuOpen = openSubmenu === item.path;
          
          return (
            <div key={item.path}>
              {hasSubmenu ? (
                <button
                  onClick={() => toggleSubmenu(item.path)}
                  className={`flex items-center justify-between w-full gap-3 px-4 py-3 mb-1 rounded transition-colors ${
                    isActive
                      ? 'text-white bg-[#3B3C42]'
                      : 'text-[#989BA4] hover:bg-[#2A2B2F] hover:text-white'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <i className={`fa ${item.icon} text-sm`} style={isActive ? {color: '#3395FF'} : {}}></i>
                    <span className="font-medium text-sm">{item.label}</span>
                  </div>
                  <i className={`fa fa-chevron-${isSubmenuOpen ? 'down' : 'right'} text-xs`}></i>
                </button>
              ) : (
                <Link
                  to={item.path}
                  onClick={() => {
                    if (item.path === '/assistant') prefetchSettings();
                    if (item.path === '/settings') prefetchSettings();
                    if (item.path === '/staff') prefetchBookings();
                    if (item.path === '/services') prefetchBookings();
                    if (item.path === '/bookings') prefetchBookings();
                    if (item.path === '/smart-dispatch') prefetchSettings();
                    if (item.path === '/logs') prefetchLogs();
                    if (item.path === '/skills') prefetchSkills();
                    if (item.path === '/plans') prefetchPlans();
                    if (item.path === '/referrals') prefetchReferrals();
                  }}
                  onMouseEnter={() => {
                    if (item.path === '/assistant') prefetchSettings();
                    if (item.path === '/settings') prefetchSettings();
                    if (item.path === '/staff') prefetchBookings();
                    if (item.path === '/services') prefetchBookings();
                    if (item.path === '/bookings') prefetchBookings();
                    if (item.path === '/smart-dispatch') prefetchSettings();
                    if (item.path === '/logs') prefetchLogs();
                    if (item.path === '/skills') prefetchSkills();
                    if (item.path === '/plans') prefetchPlans();
                    if (item.path === '/referrals') prefetchReferrals();
                  }}
                  className={`flex items-center gap-3 px-4 py-3 mb-1 rounded transition-colors ${
                    isActive
                      ? 'text-white bg-[#3B3C42]'
                      : 'text-[#989BA4] hover:bg-[#2A2B2F] hover:text-white'
                  }`}
                >
                  <i className={`fa ${item.icon} text-sm`} style={isActive ? {color: '#3395FF'} : {}}></i>
                  <span className="font-medium text-sm">{item.label}</span>
                </Link>
              )}
              
              {/* Submenu */}
              {hasSubmenu && isSubmenuOpen && (
                <div className="ml-4 mt-1 mb-2">
                  {item.submenu.map((subItem) => {
                    const isSubActive = location.pathname === subItem.path;
                    return (
                      <Link
                        key={subItem.path}
                        to={subItem.path}
                        className={`flex items-center gap-3 px-4 py-2 mb-1 rounded text-sm transition-colors ${
                          isSubActive
                            ? 'text-white bg-[#2A2B2F]'
                            : 'text-[#989BA4] hover:bg-[#2A2B2F] hover:text-white'
                        }`}
                      >
                        <span>{subItem.label}</span>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Sign Out */}
      <div className="p-3">
        <div className="border-t border-[#2A2B2F] mx-1 mb-4"></div>
        <button
          onClick={logout}
          className="flex items-center gap-3 px-4 py-3 text-[#989BA4] hover:bg-[#2A2B2F] hover:text-white rounded transition-colors w-full"
        >
          <i className="fa fa-sign-out text-sm"></i>
          <span className="font-medium text-sm">Sign Out</span>
        </button>
      </div>
    </div>
  );
};

export default Sidebar;